import videojs from 'video.js';
import { isPresentationId } from '../../util';
import { ContentStreamType } from "../../api/presentation";

export class ConvivaPluginStub extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        player.loader().onLoadPlayCover(onLoadPlayCover.bind(player));
        player.loader().onLoadPresentation(onLoadPresentation.bind(player));
    }
}

function shouldLoad(presentationId, playCoverData, presentationData) {
    const urlSearchParams = new URLSearchParams(location.search);
    const queryStringEnableTouchstone = urlSearchParams.get('useConvivaTouchstone') === 'true';

    const { playerOptions } = presentationData;

    return (playerOptions.conviva?.enabled || queryStringEnableTouchstone) && isPresentationId(presentationId);
}

function onLoadPlayCover(presentationId, playCoverData, presentationData) {
    const { conviva: convivaSettings } = presentationData.playerOptions ?? {};

    if (convivaSettings?.enabled === false || !isPresentationId(presentationId)) {
        return {
            playerOptions: {
                conviva: undefined
            }
        }
    }
}

async function onLoadPresentation(presentationId, playCoverData, presentationData) {
    const player = this;
    const urlSearchParams = new URLSearchParams(location.search);
    const queryStringEnableTouchstone = urlSearchParams.get('useConvivaTouchstone') === 'true';

    const { metadata, playerOptions, streams } = presentationData;
    const { conviva: convivaSettings } = playerOptions;

    if (shouldLoad(...arguments)) {
        if (!videojs.getPlugin('conviva')) {
            const { ConvivaPlugin } = await import(/* webpackChunkName: 'conviva' */ './conviva.js');
            player.registerPlugin('conviva', ConvivaPlugin);
        }

        const firstPlayableItem = getFirstPlayableItem(player, streams);
        const map = presentationData.compositionMap;
        const comp = map?.VisualCompositions[0];

        const convivaConfiguration = {
            presentationId,
            serviceUrl: queryStringEnableTouchstone ? DEFAULT_TOUCHSTONE_ULR_TEST : convivaSettings.convivaServiceUrl,
            customerId: convivaSettings.convivaCustomerId,
            title: metadata.title,
            isLive: player.usingPlugin('live'),
            is360: presentationData.metadata.is360Video.toString(),
            hasQuizzing: player.usingPlugin('quizzes').toString(),
            playerType: convivaSettings.playerType,
            duration: metadata.duration / 1000,
            recordedDateString: new Date(metadata.unixTime).toLocaleDateString(),
            numberOfSlides: getSlideCount(streams),
            numberOfVideos: getVideoCount(streams, comp),
            playerId: convivaSettings.playerId,
            layoutId: convivaSettings.layoutId,
            siteName: convivaSettings.siteName,
            siteVersion: convivaSettings.siteVersion,
            siteBuildNumber: convivaSettings.siteBuildNumber,
            playerName: convivaSettings.playerName,
            userNameHash: convivaSettings.userNameHash,
            multisiteId: convivaSettings.multisiteId,
            streamType: firstPlayableItem?.type,
            streamUrl: firstPlayableItem?.src
        };

        player.conviva().init(convivaConfiguration);
    }
    else {
        return {
            playerOptions: {
                conviva: undefined
            }
        }
    }
}

const DEFAULT_TOUCHSTONE_ULR_TEST = 'https://sonicfoundry-test.testonly.conviva.com';

function getFirstPlayableItem(player, streams) {
    // Find the first stream that the player thinks it can play
    let result = null;
    for (const stream of streams) {
        for (const item of stream.items) {
            if (player.canPlayType(item.type)) {
                result = item;
                break;
            }
        }
        if (result) {
            break;
        }
    }
    return result;
}

function getVideoCount(streams, composition) {
    let videoAmount = 0;

    for (const stream of streams) {
        if (stream.audioOnly === true || !findVisualItemBySourceId(composition, stream.id)) {
            //AudioOnly streams are hidden, but kept in player for audio
            //Streams not in composition are not included
            continue;
        } else if (stream.streamType !== ContentStreamType.Slide) {
            videoAmount++;
        }
    }

    return videoAmount;
}

function findVisualItemBySourceId(composition, sourceId) {
    return composition.VisualItems.find(item => item.SourceId === sourceId);
}

function getSlideCount(streams) {
    const slideStream = streams.find(stream => stream.streamType === ContentStreamType.Slide);

    if (slideStream) {
        return slideStream.items.length;
    }
    return 0;
}
